import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { AddonsParent } from '@vacationtracker/shared/types/automations'

import { isSubscribed } from '../Add-ons/common/addons'

const COMING_SOON_URL = 'coming-soon'

export interface AddonCatalogEntry {
  type: string
  parent: string
  url: string
  roles: string[]
  includedIn?: string[]
  allowViewMode?: boolean
  externalLink?: string
}

export type AddonTileStatus = 'active' | 'setupRequired' | 'inactive' | 'comingSoon'
export type AddonTileLock = 'complete' | 'coreAndComplete' | null

export type AddonTileAction =
  | { kind: 'view'; href: string }
  | { kind: 'create'; href: string }
  | { kind: 'external'; href: string }
  | { kind: 'activate' }
  | { kind: 'comingSoon' }
  | { kind: 'none' }

export interface AddonTile {
  type: string
  status: AddonTileStatus
  lock: AddonTileLock
  adminsOnly: boolean
  action: AddonTileAction
}

export interface GetAddonTilesParams {
  entries: AddonCatalogEntry[]
  plan: string
  role: string
  purchased: Record<string, string>
  withRuleOn: Set<string>
}

type TileContext = Omit<GetAddonTilesParams, 'entries'>

const lockFor = (entry: AddonCatalogEntry, context: TileContext): AddonTileLock => {
  if (entry.url === COMING_SOON_URL) return null
  if (isSubscribed(context.purchased[entry.type])) return null
  const includedIn = entry.includedIn ?? []
  if (includedIn.includes(context.plan)) return null
  return includedIn.includes(SubscriptionPlanEnum.core) ? 'coreAndComplete' : 'complete'
}

const actionFor = (entry: AddonCatalogEntry, tile: Omit<AddonTile, 'action'>, context: TileContext): AddonTileAction => {
  const viewHref = `/app/add-ons/${entry.url}/view`
  if (isSubscribed(context.purchased[entry.type])) return { kind: 'view', href: viewHref }
  if (tile.status === 'comingSoon') return { kind: 'comingSoon' }
  if (tile.adminsOnly || tile.lock) return { kind: 'none' }
  if (entry.externalLink) return { kind: 'external', href: entry.externalLink }
  if (entry.allowViewMode) return { kind: 'view', href: viewHref }
  if (entry.type === 'ENTITLEMENT_BY_ROLE' && context.plan === SubscriptionPlanEnum.core) {
    return { kind: 'create', href: '/app/add-ons/entitlement-by-role/create' }
  }
  if (entry.includedIn?.includes(context.plan)) return { kind: 'activate' }
  return { kind: 'none' }
}

// A rule left on after a downgrade does not make a locked add-on Active.
const statusFor = (entry: AddonCatalogEntry, context: TileContext, lock: AddonTileLock): AddonTileStatus => {
  if (entry.url === COMING_SOON_URL) return 'comingSoon'
  if (context.withRuleOn.has(entry.type)) return lock ? 'inactive' : 'active'
  return isSubscribed(context.purchased[entry.type]) ? 'setupRequired' : 'inactive'
}

const toTile = (entry: AddonCatalogEntry, context: TileContext): AddonTile => {
  const lock = lockFor(entry, context)
  const base = {
    type: entry.type,
    status: statusFor(entry, context, lock),
    lock,
    adminsOnly: context.role !== 'Admin' && (context.role !== 'Approver' || !entry.roles.includes('Approver')),
  }
  return { ...base, action: actionFor(entry, base, context) }
}

export const getAddonTiles = (params: GetAddonTilesParams): AddonTile[] => {
  const { entries, ...context } = params
  const { role } = context
  const tiles = entries.filter((entry) => entry.parent !== AddonsParent.integrations).map((entry) => toTile(entry, context))
  if (role === 'Admin') return tiles
  return [...tiles.filter((tile) => !tile.adminsOnly), ...tiles.filter((tile) => tile.adminsOnly)]
}

export const isAddonActive = (tile: AddonTile): boolean => tile.status === 'active'
